// The read a request belongs to, while `useQuery` asks (ADR-0419 decision 1,
// `1d`, #1861).
//
// **`useQuery` is handed a closure, so it cannot see the request the closure
// makes, nor put a `signal` on it.** What it can do is say, for the moment it
// calls the closure, which run of its effect is asking. A request started in
// that moment — every `api.GET` or `api.POST` a fetcher makes before its first
// `await` — is that run's: `client.ts` reads the run here, stops the request
// when the run is superseded, and lets an identical request from another run
// share it while both are in flight. No call site passes anything.
//
// **A request started after an `await` inside a fetcher is outside that
// moment, and is not the run's.** It is made exactly as before: its answer is
// still dropped by `useQuery`'s `latest` counter when the run is superseded,
// and it is neither stopped nor shared. `useDrained` and `drain` carry the run
// to the pages they ask for after the first (`hooks.ts`); a fetcher written as
// its own loop of awaits does not, and the hook cannot reach it without a
// change at its call site, which the ruling excludes.

/** One run of a `useQuery` effect: the signal that is aborted when the run is
 * superseded — its deps change, a `reload` starts a newer read, or the
 * component unmounts. */
export type Read = { signal: AbortSignal };

let current: Read | undefined;

/** Runs `f` with `read` as the read its requests belong to, and puts back
 * whatever was there before. `undefined` runs `f` as no read's: its requests
 * are made as if no hook were asking. */
export function within<T>(read: Read | undefined, f: () => T): T {
  const before = current;
  current = read;
  try {
    return f();
  } finally {
    current = before;
  }
}

/** The read a request started now belongs to, if any. */
export function currentRead(): Read | undefined {
  return current;
}
